import { OverlayViewF } from '@react-google-maps/api'
import React, { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'

import { shortestPathPoints, flightDirection } from './flightPathUtils'
import MapPath from './MapPath'
import CircularPin from './Pins/CircularPin'
import { MapRouteProps, getPathBounds } from '../utils'

import Clickable from 'components/Common/Clickable'
import useStableObjectReference from 'hooks/useStableObjectReference'
import { ItemMetadata } from 'tripPlanner/models/index'

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

interface Props
  extends Required<
    Omit<MapRouteProps, 'includeInMapBounds' | 'itemPlaceType'>
  > {
  map?: google.maps.Map
  isZooming: boolean
}

function MapRoute(props: Props) {
  const { active, item, onDeselect, onSelect, zIndex, map } = props
  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)

  const { startPlace, endPlace } = props.item
  const Icon = ItemMetadata[item.type].icon

  const [shortStart, shortEnd] = useStableObjectReference(
    shortestPathPoints(startPlace.point, endPlace.point),
  )

  const bounds = getPathBounds(shortStart, shortEnd)

  const refHandler: React.LegacyRef<HTMLDivElement> = (ref) => {
    ref?.parentElement?.parentElement?.classList.add('trip-map-route-container') // class imported to CP global styles
  }

  useEffect(() => {
    if (map) {
      const idleListener = map.addListener('idle', () => {
        const zoom = map.getZoom()
        const projection = map.getProjection()
        if (projection && zoom) {
          const scale = 1 << zoom
          const startPixelCoords = projection.fromLatLngToPoint(shortStart)
          const endPixelCoords = projection.fromLatLngToPoint(shortEnd)
          if (startPixelCoords && endPixelCoords) {
            const width =
              Math.abs(endPixelCoords.x - startPixelCoords.x) * scale
            const height =
              Math.abs(endPixelCoords.y - startPixelCoords.y) * scale
            setWidth(width)
            setHeight(height)
          }
        }
      })

      return () => idleListener.remove()
    }
  }, [map, shortEnd, shortStart])

  const onClick = useCallback(() => {
    if (active) {
      onDeselect()
    } else {
      onSelect()
    }
  }, [active, onDeselect, onSelect])

  useEffect(() => {
    if (active && map) {
      map.fitBounds(bounds)
    }
    // Making bounds a dependency here will keep it locked every time you try and move around on the map
    // Must exclude it to allow user to still freely roam the map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, map])

  if (!map?.getProjection()?.fromLatLngToPoint) return null

  const direction = flightDirection(shortStart, shortEnd)

  return (
    <OverlayViewF mapPaneName="overlayMouseTarget" bounds={bounds}>
      <Container ref={refHandler} style={{ zIndex: active ? 999 : zIndex }}>
        <MapPath
          props={props}
          onClick={onClick}
          pxWidth={width}
          pxHeight={height}
          direction={direction}
          marker={
            <div>
              <Clickable onClick={onClick}>
                <CircularPin isActive={active}>
                  <Icon colour="neutral-eight" />
                </CircularPin>
              </Clickable>
            </div>
          }
        />
      </Container>
    </OverlayViewF>
  )
}

export default React.memo(MapRoute)
